/**
 * CheckUp Hooks
 *
 * React Query hooks for CheckUp Hero operations.
 * Consolidated under /api/heroes/checkup/:talentId/*
 */

import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { requireJourney } from './require-journey';
import { createCheckUpService, type AnalyzeLanguageTestInput } from '../services/checkup.service';
import { homeKeys } from './home-keys';
import { heroesKeys } from './useHeroes';

/**
 * Every CheckUp read and write is ONE journey's (JOURNEYS-MULTI-ROUTE-PLAN · X6),
 * so every hook takes the journey and stays idle until it is known — a request
 * without one would read or write across every journey the citizen holds. The
 * key carries it, so a journey switch is a different cache entry.
 */
export const checkUpKeys = {
  all: ['checkups'] as const,
  assessment: (talentId: string, journeyId: string | undefined) =>
    [...checkUpKeys.all, 'assessment', talentId, journeyId ?? null] as const,
  history: (talentId: string, journeyId: string | undefined) =>
    [...checkUpKeys.all, 'history', talentId, journeyId ?? null] as const,
  /**
   * Every job-status key for a talent. Invalidate with THIS, never with a
   * single job's key — a trigger does not know the id it is about to be
   * given.
   */
  jobs: () => [...checkUpKeys.all, 'job'] as const,
  /**
   * One named calculation job. (W5.7 · F26)
   *
   * The key carries the JOB id, not just the talent. A talent-scoped status
   * key is the defect: a retake enqueues a second job and the cache then
   * serves the previous cycle's terminal status under the same key, so a
   * client that starts a new calculation can read `completed` before
   * anything has run.
   */
  job: (talentId: string, jobId: string | undefined, journeyId: string | undefined) =>
    // `null` rather than a string sentinel for "no job yet": a job id is
    // always a string, so nothing real can ever collide with it. (The query
    // is disabled in that state anyway, but a key that CAN collide is the
    // kind of assumption that only looks safe until job ids change shape.)
    [...checkUpKeys.jobs(), talentId, jobId ?? null, journeyId ?? null] as const,
  results: (talentId: string, journeyId: string | undefined) =>
    [...checkUpKeys.all, 'results', talentId, journeyId ?? null] as const,
  meshBreakdown: (talentId: string, journeyId: string | undefined) =>
    [...checkUpKeys.all, 'mesh', talentId, journeyId ?? null] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// Hero CheckUp hooks (canonical)
// ═══════════════════════════════════════════════════════════════════════

/** The journey's assessment state */
export function useCheckUpAssessment(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: checkUpKeys.assessment(talentId, journeyId),
    queryFn: () => service.getAssessment(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId,
  });
}

/** Start a new assessment on the journey */
export function useStartAssessment(journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (talentId: string) => service.startAssessment(talentId, requireJourney(journeyId)),
    onSuccess: (_data, talentId) => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.assessment(talentId, journeyId) });
    },
  });
}

/**
 * Cancel an in-progress CheckUp assessment for the given journey.
 * Soft-deactivates the assessment row server-side so the next render
 * sees status='not_started' and routes to the Welcome screen.
 *
 * Use together with `useDiscardGuide({ flowId: 'checkup' })` from the
 * lifecycle SDK so both the CoPilot guide-state AND the assessment
 * row are reset together.
 */
export function useCancelCheckUpAssessment(journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (talentId: string) => service.cancelAssessment(talentId, requireJourney(journeyId)),
    onSuccess: (_data, talentId) => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.assessment(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: checkUpKeys.all });
    },
  });
}

/** Submit the journey's motivational answers */
export function useSubmitMotivational(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (dto: { answers: Array<{ questionId: string; answer: string }> }) =>
      service.submitMotivational(talentId, dto, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.assessment(talentId, journeyId) });
    },
  });
}

/** Save the journey's motivational answers as draft (persisted to DB) */
export function useSaveDraftMotivational(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (dto: { answers: Array<{ questionId: string; answer: string }> }) =>
      service.saveDraftMotivational(talentId, dto, requireJourney(journeyId)),
  });
}

/** Load the journey's draft motivational answers from DB */
export function useLoadDraftMotivational(
  talentId: string,
  journeyId: string | undefined,
  enabled = true
) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: [...checkUpKeys.assessment(talentId, journeyId), 'draft'] as const,
    queryFn: () => service.getDraftMotivational(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId && enabled,
    staleTime: 0,
  });
}

/** Report widget completion progress on the journey */
export function useReportWidgetProgress(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (dto: {
      widgetCompletions: Record<
        string,
        { completed: boolean; completedAt?: string; data?: Record<string, unknown> }
      >;
    }) => service.reportWidgetProgress(talentId, dto, requireJourney(journeyId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.all });
    },
  });
}

/** Submit pitch video */
export function useSubmitPitchVideo(talentId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (dto: { fileId: string; duration: number }) =>
      service.submitPitchVideo(talentId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.all });
    },
  });
}

/** Analyze a language certificate (image, PDF or DOCX) for its CEFR level(s). */
export function useAnalyzeLanguageTest(talentId: string) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (dto: AnalyzeLanguageTestInput) => service.analyzeLanguageTest(talentId, dto),
  });
}

/** Trigger the journey's IQulture calculation */
export function useTriggerCalculation(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const service = createCheckUpService(client);

  return useMutation({
    mutationFn: (params?: { idempotencyKey?: string } | string) => {
      const key = typeof params === 'string' ? params : params?.idempotencyKey;
      return service.triggerCalculation(talentId, requireJourney(journeyId), key);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: checkUpKeys.jobs() });
      queryClient.invalidateQueries({ queryKey: checkUpKeys.assessment(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: checkUpKeys.results(talentId, journeyId) });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
      // Home shows "calculating" at once — and polls until the score lands —
      // instead of a cached "ready" for up to a minute (F26's other half).
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

/**
 * Watch ONE named calculation job. (W5.7 · F26)
 *
 * ── Pass a SERVER-derived `jobId` and `enabled` ────────────────────────────
 *
 * `jobId` is `checkup_assessments.calculation_job_id` (the SDK surfaces it as
 * `CheckUpAssessment.calculationJobId`), and `enabled` should be a predicate
 * over the assessment's own status. Both come off the wire, which is what
 * makes the watcher survive a remount: the page that started the calculation
 * kept the id in component state, so closing the tab, switching hero, or any
 * unmount lost the only observer the completion had — and the score sat
 * finished in the database with nothing left to refetch it. Never gate this
 * on a local "I started it" flag.
 *
 * `staleTime: 0` is not an optimisation. The SDK's global `staleTime` is one
 * minute, so a remount inside that window would render the cached status with
 * no request at all — which is exactly the window the reported F26 case
 * landed in.
 */
export function useCheckUpCalculationJob(
  talentId: string,
  jobId: string | undefined,
  journeyId: string | undefined,
  enabled = true
) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: checkUpKeys.job(talentId, jobId, journeyId),
    queryFn: () =>
      service.getCalculationJobStatus(talentId, jobId as string, requireJourney(journeyId)),
    enabled: !!talentId && !!jobId && !!journeyId && enabled,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // The server's in-progress vocabulary (`IN_PROGRESS_JOB_STATUSES`).
      // `pending` is the column default and is reachable, so polling that
      // stopped at queued/processing could park on a job that had not been
      // picked up yet.
      return status === 'pending' || status === 'queued' || status === 'processing' ? 2000 : false;
    },
  });
}

/** A job status from which the calculation will not move again. */
export function isTerminalCalculationStatus(status: string | undefined): boolean {
  return status === 'completed' || status === 'failed';
}

/**
 * Refresh everything a finished CheckUp calculation changes. (F26)
 *
 * Every CheckUp read — assessment, results, latest result, history,
 * the MESH and pillar breakdowns, for EVERY talent/journey
 * variant a screen keyed it under — except the job-status keys, which are
 * terminal by now. Plus the places a completion lands outside CheckUp:
 * Miles (the completion awards them), the Passport (its timeline and stamps
 * project from the completion's IRR event), the MESH summary, and Home (its
 * moment moves from getting ready to the journey dashboard).
 */
export async function invalidateCheckUpCalculationOutcome(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: checkUpKeys.all,
      predicate: (query) => query.queryKey[1] !== 'job',
    }),
    queryClient.invalidateQueries({ queryKey: heroesKeys.miles() }),
    queryClient.invalidateQueries({ queryKey: heroesKeys.passport() }),
    queryClient.invalidateQueries({ queryKey: [...heroesKeys.all, 'stamps'] }),
    queryClient.invalidateQueries({ queryKey: homeKeys.all }),
  ]);
}

/**
 * The ONE observer that brings a finished CheckUp calculation into the cache.
 * (F26)
 *
 * Mount it ONCE per signed-in app, at journey level (journey-web's app shell,
 * the mobile root layout) — never on a screen. The calculation runs for
 * minutes, and the citizen does not wait on the CheckUp page: while only that
 * page watched the job, leaving it meant nothing was watching when the score
 * landed, so the dashboard card sat on "calculating" and a return to /checkup
 * inside the cache window offered Calculate again over a finished score. With
 * this mounted above the router, every screen — the CheckUp page, the dashboard
 * card, the mobile screens — only READS the cache, and the cache is refreshed
 * wherever the citizen happens to be when the job ends.
 *
 * Its inputs are server state only: the job is the one the assessment row names
 * (`calculationJobId`) and it is watched only while the assessment says
 * `calculating`. Reconciled once per job id, so a terminal status cached from a
 * previous cycle can never be mistaken for this one's.
 *
 * `talentId` must be the identifier the screens key their CheckUp reads with;
 * an empty string keeps it idle (e.g. while the profile loads).
 */
export function useCheckUpCalculationReconciler(
  talentId: string,
  journeyId: string | undefined
): void {
  const queryClient = useQueryClient();
  const assessment = useCheckUpAssessment(talentId, journeyId);
  const assessmentStatus = assessment.data?.status;
  const calculationJobId = assessment.data?.calculationJobId;
  const job = useCheckUpCalculationJob(
    talentId,
    calculationJobId,
    journeyId,
    assessmentStatus === 'calculating'
  );
  const jobStatus = job.data?.status;
  const reconciledJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!calculationJobId || assessmentStatus !== 'calculating') return;
    if (!isTerminalCalculationStatus(jobStatus)) return;
    if (reconciledJobIdRef.current === calculationJobId) return;
    reconciledJobIdRef.current = calculationJobId;
    void invalidateCheckUpCalculationOutcome(queryClient);
  }, [calculationJobId, assessmentStatus, jobStatus, queryClient]);
}

/** The journey's full CheckUp results */
export function useCheckUpResults(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: checkUpKeys.results(talentId, journeyId),
    queryFn: () => service.getResults(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId,
  });
}

/** The journey's MESH breakdown */
export function useMeshBreakdown(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: checkUpKeys.meshBreakdown(talentId, journeyId),
    queryFn: () => service.getMeshBreakdown(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId,
  });
}

/** The journey's assessment history */
export function useCheckUpHistory(talentId: string, journeyId: string | undefined) {
  const { client } = useSdkContext();
  const service = createCheckUpService(client);

  return useQuery({
    queryKey: checkUpKeys.history(talentId, journeyId),
    queryFn: () => service.getHistory(talentId, requireJourney(journeyId)),
    enabled: !!talentId && !!journeyId,
  });
}
